// ✅ src/pages/VehicleV2/MaintenanceDetails.jsx
// FULL UPDATED FILE
// ✅ Uses localStorage for branchId / branchType / userId / finYear
// ✅ Sends backend-matching headers in every API call
// ✅ Keeps your same UI/template/style
// ✅ Reads querystring params and applies lock behavior

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/maintenanceDetails";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const isFuture = (iso) => {
  const s = toStr(iso);
  if (!s) return false;
  return new Date(s) > new Date(todayISO());
};

export default function MaintenanceDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ Read query params
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const qp = useMemo(() => {
    const mainVehicleId = Number(qs.get("mainvehicleid") || 0) || 0;
    const mainBranchId = toStr(qs.get("mainbranchid") || qs.get("branchId") || "Select");
    const vehCode = toStr(qs.get("vehCode") || "");
    const vehName = toStr(qs.get("vehName") || "");
    const regNo = toStr(qs.get("regNo") || "");

    return { mainVehicleId, mainBranchId, vehCode, vehName, regNo };
  }, [qs]);

  // ✅ user context from localStorage
  const [userCtx] = useState(() => {
    const lsBranchId = Number(localStorage.getItem("branchId")) || 0;
    const lsBranchType = toStr(localStorage.getItem("branchType")) || "HO";
    const lsLoginUserId =
      Number(localStorage.getItem("userId")) ||
      Number(localStorage.getItem("loginuserid")) ||
      0;

    const ssMainVehicleId = Number(sessionStorage.getItem("mainvehicleid")) || 0;

    return {
      branchId: lsBranchId,
      branchType: lsBranchType,
      loginUserId: lsLoginUserId,
      mainVehicleId: qp.mainVehicleId || ssMainVehicleId,
    };
  });

  // ✅ Persist useful query params into sessionStorage
  useEffect(() => {
    if (qp.mainVehicleId) sessionStorage.setItem("mainvehicleid", String(qp.mainVehicleId));
    if (qp.mainBranchId && !isSelect(qp.mainBranchId)) {
      sessionStorage.setItem("mainbranchid", String(qp.mainBranchId));
    }
    if (qp.vehCode) sessionStorage.setItem("mainvehcode", qp.vehCode);
    if (qp.vehName) sessionStorage.setItem("mainvehname", qp.vehName);
    if (qp.regNo) sessionStorage.setItem("mainvehregno", qp.regNo);
  }, [qp.mainVehicleId, qp.mainBranchId, qp.vehCode, qp.vehName, qp.regNo]);

  const [showFilters, setShowFilters] = useState(true);

  const [dd, setDd] = useState({
    branches: [{ label: "Select", value: "Select" }],
    subAssemblies: [{ label: "Select", value: "Select" }],
    descriptions: [{ label: "Select", value: "Select" }],
  });

  const [userLock, setUserLock] = useState({
    branch: false,
    branchValue: "Select",
    vehCode: "",
    vehName: "",
    regNo: "",
    disableAllVehicleFields: false,
  });

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    branch: "Select",
    regNo: "",
    vehicleName: "",
    vehicleCode: "",
    subAssembly: "Select",
    subAssemblyText: "Select",
    description: "Select",
    descriptionText: "Select",
  });

  const [ac, setAc] = useState({
    vehicleNames: [],
    regNos: [],
    vehicleCodes: [],
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const normalizeOptionList = (arr, fallbackLabelKey, fallbackValueKey) => {
    const list = Array.isArray(arr) ? arr : [];
    return list.map((x) => {
      if (typeof x === "string") return { label: x, value: x };
      const label =
        x.label ??
        x[fallbackLabelKey] ??
        x.value ??
        x[fallbackValueKey] ??
        "";
      const value =
        x.value ??
        x[fallbackValueKey] ??
        x.label ??
        x[fallbackLabelKey] ??
        "";
      return { label: String(label), value: String(value) };
    });
  };

  const validateDates = (fromDate, toDate) => {
    if (fromDate && isFuture(fromDate)) {
      toast.error("Select From date lesser than current date");
      return false;
    }
    if (toDate && isFuture(toDate)) {
      toast.error("Select To date lesser than current date");
      return false;
    }
    if (fromDate && toDate) {
      if (new Date(toDate) < new Date(fromDate)) {
        toast.error("Please select From date less than To date");
        return false;
      }
    }
    return true;
  };

  const loadInit = async () => {
    try {
      const params = {
        branchId: userCtx.branchId,
        branchType: userCtx.branchType,
        loginUserId: userCtx.loginUserId,
        mainVehicleId: userCtx.mainVehicleId,
      };

      const { data } = await axios.get(`${API_BASE}/init`, {
        headers: apiHeaders,
        params,
      });

      if (!data?.success) throw new Error(data?.message || "Init failed");

      const branches = normalizeOptionList(
        data.branches,
        "fld_branchname",
        "fld_branchid"
      );
      const subAssemblies = normalizeOptionList(
        data.subAssemblies,
        "fld_SubAssemblyName",
        "fld_SubAssemblyid"
      );

      setDd((p) => ({
        ...p,
        branches: branches.length ? branches : [{ label: "Select", value: "Select" }],
        subAssemblies: subAssemblies.length
          ? subAssemblies
          : [{ label: "Select", value: "Select" }],
        descriptions: [{ label: "Select", value: "Select" }],
      }));

      const lock = data.userLock || {};

      const lockedBranchValue =
        !isSelect(qp.mainBranchId) && qp.mainBranchId
          ? qp.mainBranchId
          : toStr(lock.branchValue || "Select");

      const lockedVehCode = qp.vehCode || toStr(lock.vehCode);
      const lockedVehName = qp.vehName || toStr(lock.vehName);
      const lockedRegNo = qp.regNo || toStr(lock.regNo);

      const shouldLockVehicleFields = !!(lockedVehCode || lockedVehName || lockedRegNo);

      setUserLock({
        branch:
          !!lock.branch || (!isSelect(lockedBranchValue) && userCtx.branchType === "HO"),
        branchValue: lockedBranchValue,
        vehCode: lockedVehCode,
        vehName: lockedVehName,
        regNo: lockedRegNo,
        disableAllVehicleFields:
          !!lock.disableAllVehicleFields || shouldLockVehicleFields,
      });

      setFilters((p) => {
        const next = { ...p };

        if (userCtx.branchType !== "HO") {
          next.branch = String(userCtx.branchId);
        } else if (!isSelect(lockedBranchValue) && lockedBranchValue) {
          next.branch = String(lockedBranchValue);
        } else if (!isSelect(lock.branchValue) && lock.branchValue) {
          next.branch = String(lock.branchValue);
        } else if (p.branch === "Select") {
          next.branch = "Select";
        }

        if (lockedRegNo) next.regNo = lockedRegNo;
        if (lockedVehName) next.vehicleName = lockedVehName;
        if (lockedVehCode) next.vehicleCode = lockedVehCode;

        return next;
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load init");
    }
  };

  const loadDescriptions = async (subAssemblyId) => {
    try {
      if (!subAssemblyId || isSelect(subAssemblyId)) {
        setDd((p) => ({
          ...p,
          descriptions: [{ label: "Select", value: "Select" }],
        }));
        return;
      }

      const params = {
        mainVehicleId: userCtx.mainVehicleId,
        subAssemblyId,
      };

      const { data } = await axios.get(`${API_BASE}/descriptions`, {
        headers: apiHeaders,
        params,
      });

      if (!data?.success) throw new Error(data?.message || "Failed to load descriptions");

      const descriptions = normalizeOptionList(
        data.descriptions,
        "fld_Description",
        "fld_vehiclemaindetid"
      );

      setDd((p) => ({
        ...p,
        descriptions: [
          { label: "Select", value: "Select" },
          ...descriptions.filter((x) => !isSelect(x.value)),
        ],
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load descriptions");
      setDd((p) => ({
        ...p,
        descriptions: [{ label: "Select", value: "Select" }],
      }));
    }
  };

  const loadList = async (overrides = {}) => {
    try {
      const f = { ...filters, ...overrides };

      if (!validateDates(f.fromDate, f.toDate)) return;

      const subAssemblyText =
        dd.subAssemblies.find((x) => String(x.value) === String(f.subAssembly))?.label ||
        f.subAssemblyText ||
        "Select";

      const descriptionText =
        dd.descriptions.find((x) => String(x.value) === String(f.description))?.label ||
        f.descriptionText ||
        "Select";

      const params = {
        mainVehicleId: userCtx.mainVehicleId,
        branchType: userCtx.branchType,
        branch:
          userCtx.branchType === "HO"
            ? isSelect(f.branch)
              ? "Select"
              : f.branch
            : String(userCtx.branchId),

        fromDate: f.fromDate,
        toDate: f.toDate,
        regNo: f.regNo,
        vehicleName: f.vehicleName,
        vehicleCode: f.vehicleCode,
        subAssemblyText: isSelect(subAssemblyText) ? "Select" : subAssemblyText,
        descriptionText: isSelect(descriptionText) ? "Select" : descriptionText,
        page,
        pageSize,
      };

      const { data } = await axios.get(`${API_BASE}/list`, {
        headers: apiHeaders,
        params,
      });

      if (!data?.success) throw new Error(data?.message || "List failed");

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load maintenance details");
      setRows([]);
      setTotal(0);
    }
  };

  const loadAC = async (kind, q) => {
    try {
      const s = toStr(q);
      if (s.length < 1) return;

      const baseParams = {
        branchId: userCtx.branchId,
        mainVehicleId: userCtx.mainVehicleId,
        q: s,
      };

      let url = "";
      let params = { ...baseParams };

      if (kind === "vehiclename") {
        url = `${API_BASE}/ac/vehiclename`;
        params.vehRegNo = toStr(filters.regNo) || "%";
      } else if (kind === "vehicleregno") {
        url = `${API_BASE}/ac/vehicleregno`;
        params.vehName = toStr(filters.vehicleName) || "%";
      } else if (kind === "vehiclecode") {
        url = `${API_BASE}/ac/vehiclecode`;
      } else {
        return;
      }

      const { data } = await axios.get(url, {
        headers: apiHeaders,
        params,
      });

      if (!data?.success) return;

      const items = Array.isArray(data.items)
        ? data.items.map((x) => String(x))
        : [];

      setAc((p) => {
        if (kind === "vehiclename") return { ...p, vehicleNames: items };
        if (kind === "vehicleregno") return { ...p, regNos: items };
        if (kind === "vehiclecode") return { ...p, vehicleCodes: items };
        return p;
      });
    } catch {
      // ignore silently
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleChange = (e) => {
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const onSubAssemblyChanged = async (val) => {
    const label =
      dd.subAssemblies.find((x) => String(x.value) === String(val))?.label ||
      "Select";

    setFilters((p) => ({
      ...p,
      subAssembly: val,
      subAssemblyText: label,
      description: "Select",
      descriptionText: "Select",
    }));

    await loadDescriptions(val);
  };

  const onDescriptionChanged = (val) => {
    const label =
      dd.descriptions.find((x) => String(x.value) === String(val))?.label ||
      "Select";

    setFilters((p) => ({
      ...p,
      description: val,
      descriptionText: label,
    }));
  };

  const handleSearch = async () => {
    setPage(1);
    await loadList({});
    toast.success("Search completed");
  };

  const handleViewAll = async () => {
    const reset = {
      fromDate: "",
      toDate: "",
      branch: userCtx.branchType === "HO" ? "Select" : String(userCtx.branchId),
      regNo: userLock.regNo || "",
      vehicleName: userLock.vehName || "",
      vehicleCode: userLock.vehCode || "",
      subAssembly: "Select",
      subAssemblyText: "Select",
      description: "Select",
      descriptionText: "Select",
    };

    setFilters(reset);
    setDd((p) => ({
      ...p,
      descriptions: [{ label: "Select", value: "Select" }],
    }));
    setPage(1);

    await loadList(reset);
    toast.info("Showing all records");
  };

  const handleClearTextFilters = () => {
    setFilters((p) => ({
      ...p,
      regNo: userLock.regNo || "",
      vehicleName: userLock.vehName || "",
      vehicleCode: userLock.vehCode || "",
    }));
    toast.info("Cleared text filters");
  };

  const handlePrint = () => {
    navigate("/vehicle/maintenanceDetailsPrint", {
      state: {
        mainVehicleId: userCtx.mainVehicleId,
        branch: filters.branch,
        regNo: filters.regNo,
        vehicleName: filters.vehicleName,
        vehicleCode: filters.vehicleCode,
        subAssembly: filters.subAssembly,
        subAssemblyText: filters.subAssemblyText,
        description: filters.description,
        descriptionText: filters.descriptionText,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        page,
        pageSize,
        total,
      },
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const pageButtons = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (page <= 3) return [1, 2, 3, 4, 5];
    if (page >= totalPages - 2) {
      return [
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [page - 2, page - 1, page, page + 1, page + 2];
  }, [page, totalPages]);

  const isBranchDisabled = userCtx.branchType !== "HO" || userLock.branch;

  const isVehicleFieldsDisabled =
    userLock.disableAllVehicleFields ||
    !!userLock.regNo ||
    !!userLock.vehName ||
    !!userLock.vehCode;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">MAINTENANCE DETAILS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div className="flex items-center gap-3">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Maintenance Details
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                    max={todayISO()}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                    max={todayISO()}
                  />
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBranchDisabled}
                  >
                    {dd.branches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input
                    list="acRegNo"
                    type="text"
                    name="regNo"
                    value={filters.regNo}
                    onChange={(e) => {
                      handleChange(e);
                      loadAC("vehicleregno", e.target.value);
                    }}
                    className={inputClass}
                    placeholder="Enter registration number"
                    disabled={isVehicleFieldsDisabled && !!userLock.regNo}
                  />
                  <datalist id="acRegNo">
                    {ac.regNos.map((x, i) => (
                      <option key={i} value={x} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Sub Assembly</label>
                  <select
                    name="subAssembly"
                    value={filters.subAssembly}
                    onChange={(e) => onSubAssemblyChanged(e.target.value)}
                    className={inputClass}
                  >
                    {dd.subAssemblies.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    list="acVehName"
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={(e) => {
                      handleChange(e);
                      loadAC("vehiclename", e.target.value);
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                    disabled={isVehicleFieldsDisabled && !!userLock.vehName}
                  />
                  <datalist id="acVehName">
                    {ac.vehicleNames.map((x, i) => (
                      <option key={i} value={x} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Description</label>
                  <select
                    name="description"
                    value={filters.description}
                    onChange={(e) => onDescriptionChanged(e.target.value)}
                    className={inputClass}
                  >
                    {dd.descriptions.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    list="acVehCode"
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={(e) => {
                      handleChange(e);
                      loadAC("vehiclecode", e.target.value);
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    disabled={isVehicleFieldsDisabled && !!userLock.vehCode}
                  />
                  <datalist id="acVehCode">
                    {ac.vehicleCodes.map((x, i) => (
                      <option key={i} value={x} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="flex flex-col gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                  <button
                    onClick={handleClearTextFilters}
                    className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                  >
                    Clear Text Filters
                  </button>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleViewAll}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                    >
                      <FaEye />
                      View All
                    </button>

                    <button
                      onClick={handleSearch}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                    >
                      <FaSearch />
                      Search Records
                    </button>

                    <button
                      onClick={handlePrint}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                    >
                      <FaPrint />
                      Print
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-6">
                  <button
                    onClick={() => window.open("/vehicle/BatteryCardMaintenanceVehicleLogBook")}
                    className="font-bold text-green-900 dark:text-green-200 hover:underline"
                  >
                    Battery Maintenance
                  </button>

                  <button
                    onClick={() => window.open("/vehicle/SparesDetails")}
                    className="font-bold text-green-900 dark:text-green-200 hover:underline"
                  >
                    Spares and Lubricants
                  </button>

                  <button
                    onClick={() => window.open("/vehicle/TyreMaintenanceDetailForVehicleLogBook")}
                    className="font-bold text-green-900 dark:text-green-200 hover:underline"
                  >
                    Tyre Maintenance
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Maintenance Records Found
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Try adjusting your filters or click "View All".
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">Slno</th>
                      <th className="px-4 py-4 text-center font-semibold">SubAssembly Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Description</th>
                      <th className="px-4 py-4 text-center font-semibold">Oil Grade/Part No</th>
                      <th className="px-4 py-4 text-center font-semibold">Last Changed Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Last Changed Hr/KM</th>
                      <th className="px-4 py-4 text-center font-semibold">Usage Limit</th>
                      <th className="px-4 py-4 text-center font-semibold">Next Service Hr/KM</th>
                      <th className="px-4 py-4 text-center font-semibold">Current Odometer Hr/KM</th>
                      <th className="px-4 py-4 text-center font-semibold">Remaining Hr</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_vehicle_subassemblyname ?? r.SubAssemblyName ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_Description ?? r.Description ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_partnumbergrade ?? r.OilGradePartNo ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_curdate ?? r.LastChangedDate ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_hrmtrreading ?? r.LastChangedHrKm ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_usage ?? r.UsageLimit ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_usagelimit ?? r.NextServiceHrKm ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_hrskm ?? r.CurrentOdometerHrKm ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.fld_Remaininghr ?? r.RemainingHr ?? ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Page {page} of {totalPages} — Showing {(page - 1) * pageSize + 1} to{" "}
                {Math.min(page * pageSize, total)} of {total} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    page === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {pageButtons.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1 border rounded text-sm ${
                      page === p
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    page === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}